// 81 §7 — THE LINE THAT MAKES THE WHOLE FEATURE WORK, IN ONE PLACE.
//
// §7: "On signing in, in the notifications popup:
//
//        Your previous login was on DD/MM/YYYY at HH:MM
//
//      THE PREVIOUS ONE, NOT THE CURRENT ONE. Showing 'now' is the classic bug
//      and is useless. It is the second-most-recent success at the moment the
//      popup renders.
//
//      This is the cheapest anomaly detection there is: nobody audits a log, and
//      everybody notices a date they were asleep on.
//
//      Beside it, when the number is not zero: the count of failed attempts
//      since that previous login. That is the probe warning delivered at the one
//      moment the person is paying attention."
//
// ── WHY THIS IS A COMPONENT AND NOT A SECOND COPY OF THE JSX ────────────────
//
// [RULED — Laurent, 2026-08-14, on the glance of #372] the line goes in the
// DAILY-OVERVIEW MODAL as well as the bell, "because the bell needs a click, and
// the whole point of this line is that it is seen without anyone looking for
// it": the overview is what appears on a morning, unbidden. And with it:
// **"One source, rendered twice; do not write the sentence a second time."**
//
// That is not tidiness. Two copies of a sentence are two chances for one of them
// to drift into showing the CURRENT sign-in — which is the exact bug §7 names,
// and it would appear in only one of the two surfaces, on the surface nobody
// checked. There is one component, one set of keys, one decision about which row
// is "previous", and it is made on the server (routes/authAttempts.js takes two
// successes and reads `[1]`). Nothing here chooses.
//
// ── THE ONE THING THAT DIFFERS BETWEEN THE TWO PLACES ──────────────────────
//
// Its frame. In the bell it is a row in the dropdown, separated by the same
// border the rows use; in the modal it sits against the modal's own padding
// with a little more air. `variant` carries exactly that and nothing else — no
// wording, no logic, no second opinion about what to show.
//
// ── BRIEF 126 §5 — IT IS AT THE FOOT OF BOTH NOW ───────────────────────────
//
// [RULED] "Votre connexion précédente date du…" led both panels; it moves to
// the foot of both. So the rule moved from `borderBottom` to `borderTop` in
// both variants, because a separator separates from whatever is on the other
// side of it and the other side is now above.
//
// WHY THE MOVE DOES NOT UNDO 81 §7. That ruling was about the line being
// SEEN WITHOUT ANYONE LOOKING FOR IT, which is why it is in the unbidden
// morning modal at all and not behind the bell's click alone. Both panels are
// short enough to read whole — the modal is 480px wide and its groups are a
// triage, the bell caps at 600px — so "at the foot" is still on the screen the
// reader is already looking at. §3 sends the day's quote the other way for the
// mirror-image reason: one is a pleasantry that opens the day, this is a fact
// you check.
import { useTranslation } from "../i18n";
import { FS } from "../lib/tokens";
import type { AuthAttemptSummary } from "../api";

export default function SignInLine({
  summary,
  variant = "popup",
}: {
  summary: AuthAttemptSummary | null;
  /** `popup` — a row inside the bell dropdown. `modal` — above the first section. */
  variant?: "popup" | "modal";
}) {
  const { t, locale } = useTranslation();

  // Absent rather than wrong. The summary fetch fails closed (App.tsx swallows
  // it), and a line that guessed would be worse than no line at all.
  if (!summary) return null;

  const when = summary.previousLogin
    ? new Date(summary.previousLogin.lastAt).toLocaleString(locale, {
        // Formatted for the READER'S locale — i18n.tsx's LOCALE_BY_LANG maps
        // en→en-GB, fr→fr-BE, nl→nl-BE — and never a hard-coded DD/MM/YYYY (§7).
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

  return (
    <div
      style={
        variant === "modal"
          ? {
              padding: "10px 0 0",
              marginTop: 10,
              borderTop: "1px solid var(--br)",
              fontSize: FS.s10_5,
              color: "var(--tm)",
              lineHeight: 1.5,
            }
          : {
              padding: "8px 10px",
              borderTop: "1px solid var(--br)",
              fontSize: FS.s10_5,
              color: "var(--tm)",
              lineHeight: 1.5,
            }
      }
    >
      {summary.previousLogin ? (
        <span>
          {t("attempts.previous_login", {
            d: when,
            fallback: `Your previous sign-in was on ${when}`,
          })}
        </span>
      ) : (
        // §7 — "First-ever login has no previous. It gets its own sentence, in
        // three languages. Not a blank, not an epoch date."
        <span>
          {t("attempts.first_login", {
            fallback: "This is the first sign-in recorded for your account.",
          })}
        </span>
      )}
      {/* QUIET WHEN CLEAN, RED WHEN NOT — the treatment the bell already used,
          and the reason the count is conditional rather than "0 failed
          attempts": a zero every morning is how a reader stops seeing the
          number on the morning it is not zero. */}
      {summary.failedSince > 0 && (
        <span style={{ color: "var(--rd)", marginLeft: 6 }}>
          {t("attempts.failed_since", {
            count: summary.failedSince,
            n: summary.failedSince,
            fallback: `${summary.failedSince} failed attempt(s) on your account since then.`,
          })}
        </span>
      )}
    </div>
  );
}
